import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { formatErrorDetail } from "@/lib/format-error";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Ban, RefreshCw, RotateCcw, Search } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ReasonBadge = ({ reason }) => {
  const map = {
    user_unsubscribe: { label: "User unsubscribed", style: "bg-amber-100 text-amber-700" },
    admin: { label: "Added by admin", style: "bg-slate-100 text-slate-700" },
  };
  const { label, style } = map[reason] || { label: reason || "—", style: "bg-slate-100 text-slate-700" };
  return <Badge className={`${style} hover:${style}`}>{label}</Badge>;
};

const Suppressions = ({ user, setUser }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [confirmResubscribe, setConfirmResubscribe] = useState(null);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/suppressions`, { withCredentials: true, headers: authHeaders });
      setRows(res.data.suppressions || []);
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to load suppressions"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleResubscribe = async (row) => {
    try {
      await axios.delete(`${API}/suppressions/${row.id}`, { withCredentials: true, headers: authHeaders });
      toast.success(`${row.email} can now receive emails again`);
      setConfirmResubscribe(null);
      fetchRows();
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to resubscribe"));
    }
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.email || "").toLowerCase().includes(q) ||
      (r.recipient_name || "").toLowerCase().includes(q),
    );
  }, [rows, filter]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="suppressions-page">
      <Sidebar user={user} onLogout={handleLogout} />
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Suppressions
            </h1>
            <p className="text-slate-500 mt-1">
              People who have unsubscribed. They will not receive any future emails until you resubscribe them.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Ban className="w-5 h-5 text-amber-600" /> Do-not-send list</span>
              <Button variant="ghost" size="sm" onClick={fetchRows} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </CardTitle>
            <CardDescription>
              {rows.length} unsubscribed {rows.length === 1 ? "address" : "addresses"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative mb-4 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by email or name…"
                className="pl-9"
              />
            </div>

            {loading ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-slate-500 text-sm">
                {rows.length === 0 ? "No one has unsubscribed yet." : "No matches for that filter."}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Last template</TableHead>
                    <TableHead>Unsubscribed</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.email}</TableCell>
                      <TableCell className="text-sm">{r.recipient_name || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell><ReasonBadge reason={r.reason} /></TableCell>
                      <TableCell className="text-sm text-slate-600">{r.source_template_name || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-slate-500 text-xs">{formatDate(r.suppressed_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmResubscribe(r)}
                          className="text-emerald-700 hover:text-emerald-800"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" /> Resubscribe
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      <AlertDialog open={!!confirmResubscribe} onOpenChange={(open) => !open && setConfirmResubscribe(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resubscribe this address?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{confirmResubscribe?.email}</strong> will be removed from the do-not-send list.
              Future emails to this address will go through. They won't be re-enrolled automatically in sequences where their enrolment was cancelled, but you can enrol them by hand from Email Tools.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleResubscribe(confirmResubscribe)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Resubscribe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Suppressions;
